import React, { useEffect, useState } from "react"
import {
    Box,
    CssBaseline,
    Grid,
    ThemeProvider,
    Typography,
} from "@mui/material"
import ButtonWithIcon from "src/v2/components/Button/Button"
import { ReactComponent as CheckIcon } from "src/v2/assets/icons/check-icon.svg"
import { ReactComponent as LockEmptyIcon } from "src/v2/assets/icons/lock-empty-icon.svg"
import { ReactComponent as EyeOpen } from "src/v2/assets/icons/eye-open.svg"
import { ReactComponent as EyeClose } from "src/v2/assets/icons/eye-close.svg"
import { Logo } from "../../components/Logo/logo"
import InputWithIcon from "../../components/Input/inputWithIcon"
import { Link, useLocation, useNavigate } from "react-router-dom"
import AuthService from "../../../services/auth-service"
import theme from "../../theme/theme"
import { validatePassword } from "../../utils/validate-password/validate-password"
import { ReactComponent as ArrowLeftIcon } from "src/v2/assets/icons/arrow-left-icon.svg"
import LoaderOverlay from "../../components/LoaderOverlay/loader-overlay"
import Loader from "../../components/Loader/loader"
import { useDocumentTitle } from "../../hooks/useDocumentTitle"

const RegisterPasswordPage = () => {
    const [isEnabledButtonRegisterPassword, setIsEnableButtonRegisterPassword] =
        useState(true)
    const [isVisiblePassword, setIsVisiblePassword] = useState(false)
    const [isErrorRegisterPassword, setIsErrorRegisterPassword] =
        useState(false)
    const [confirmPassword, setConfirmPassword] = useState("")
    const [password, setPassword] = useState("")
    const [isExpiredToken, setIsExpiredToken] = useState(false)

    const location = useLocation()
    const params = new URLSearchParams(location.search)

    useEffect(() => {
        if(validatePassword(confirmPassword) || validatePassword(password)) {
            setIsErrorRegisterPassword(false)
        }
    }, [confirmPassword, password])
    const togglePasswordVisibility = () => {
        setIsVisiblePassword(
            (previousVisiblePassword) => !previousVisiblePassword
        )
    }

    const navigate = useNavigate()

    const authService = new AuthService()

    const isMatchPassword = confirmPassword === password
    useDocumentTitle("Recuperação de Senha")

    const submitHandlerRegisterPassword = async (e) => {
        e.preventDefault()
        if (!confirmPassword || !password) return
        setIsEnableButtonRegisterPassword(false)


        const uid = params.get("uid")
        const token = params.get("token")

        try {
            await authService.registerNewPassword(uid, token, password)
            navigate("/", { state: "passwordChange" })
        } catch (e) {
            if (
                e &&
                e.response &&
                typeof e.response.text === "string"
            ) {
                let errorMessage = JSON.parse(e.response.text)
                if (
                    errorMessage &&
                    errorMessage.token &&
                    errorMessage.token[0] === "Token inválido para o usuário fornecido."
                ) {
                    setIsExpiredToken(true)
                }
                setIsErrorRegisterPassword(true)

            }
        } finally {
            setIsEnableButtonRegisterPassword(true)
        }
    }

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />

            <Grid container>
                <Grid
                    item
                    sm={4}
                    md={12}
                    sx={{
                        backgroundImage: 'url("/background/login.webp")',
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        height: "100vh",
                        width: "100%",
                    }}
                    alignContent="center"
                >
                    {!isEnabledButtonRegisterPassword && (
                        <LoaderOverlay>
                            <Loader />
                        </LoaderOverlay>
                    )}
                    <Box
                        maxWidth="388px"
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        p={3}
                        bgcolor={theme.palette.text.primary}
                        ml={{ xs: 2, sm: 3, md: 2, lg: 16 }}
                        mr={{ xs: 2, sm: 3, md: 2, lg: 16 }}
                        height={{ md: "100%", xs: "auto" }}
                        sx={{
                            "@media (min-width:420px) and (max-width:833px)": {
                                margin: "auto",
                            },
                        }}
                    >
                        <Logo />
                        {isExpiredToken ? (
                            <Box
                                display="flex"
                                flexDirection="column"
                                justifyContent="center"
                                alignItems="center"
                                gap={3}
                                sx={{ width: "100%" }}
                            >
                                <Box mt={2.25} gap={3}>
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        textAlign="center"
                                        gap={3}
                                    >
                                        <Typography
                                            variant={"h3"}
                                            color="common.white"
                                        >
                                            Token expirado
                                        </Typography>
                                        <Typography
                                            variant="textBody"
                                            color="common.white"
                                        >
                                            Seu token de recuperação de senha
                                            expirou. <br />
                                            Solicite um novo link ao
                                            administrador.
                                        </Typography>
                                        <ButtonWithIcon
                                            icon={<ArrowLeftIcon />}
                                            size={"lg"}
                                            component={Link}
                                            to="/"
                                            sx={{
                                                width: "min-content",
                                                margin: "auto",
                                            }}
                                        >
                                            VOLTAR
                                        </ButtonWithIcon>
                                    </Box>
                                </Box>
                            </Box>
                        ) : (
                            <Box
                                component={"form"}
                                onSubmit={submitHandlerRegisterPassword}
                                autoComplete="off"
                                display="flex"
                                flexDirection="column"
                                gap={2.5}
                                noValidate
                                sx={{ width: "100%" }}
                            >
                                <Box mt={2.25} gap={2.5}>
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        gap={2.5}
                                    >
                                        <Typography
                                            variant="textBody"
                                            color="common.white"
                                            sx={{
                                                textAlign: "center",
                                            }}
                                        >
                                            Os campos precisam ser iguais
                                        </Typography>
                                        <InputWithIcon
                                            type={
                                                isVisiblePassword
                                                    ? "text"
                                                    : "password"
                                            }
                                            label="Nova senha"
                                            startIcon={<LockEmptyIcon />}
                                            onChange={(e) =>
                                                setPassword(e.target.value)
                                            }
                                            value={password}
                                            error={isErrorRegisterPassword}
                                            helperText={"Senha inválida"}
                                        />
                                        <InputWithIcon
                                            type={
                                                isVisiblePassword
                                                    ? "text"
                                                    : "password"
                                            }
                                            label="Confirme a senha"
                                            startIcon={<LockEmptyIcon />}
                                            onChange={(e) =>
                                                setConfirmPassword(
                                                    e.target.value
                                                )
                                            }
                                            value={confirmPassword}
                                            endIcon={
                                                isVisiblePassword ? (
                                                    <EyeClose />
                                                ) : (
                                                    <EyeOpen />
                                                )
                                            }
                                            onEndIconClick={
                                                togglePasswordVisibility
                                            }
                                            error={isErrorRegisterPassword}
                                            helperText={"Senha inválida"}
                                        />
                                    </Box>
                                </Box>
                                <Box
                                    alignItems="end"
                                    display="flex"
                                    flexDirection="row"
                                    justifyContent="end"
                                >
                                    <ButtonWithIcon
                                        type={"submit"}
                                        size={"lg"}
                                        icon={<CheckIcon />}
                                        disabled={
                                            !isEnabledButtonRegisterPassword ||
                                            !isMatchPassword ||
                                            !validatePassword(
                                                confirmPassword
                                            ) ||
                                            !validatePassword(password)
                                        }
                                    >
                                        CONFIRMAR
                                    </ButtonWithIcon>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </ThemeProvider>
    )
}

export default RegisterPasswordPage
